/*
 * R160jj · Feb 2026 — Board Templates (server-synced).
 *
 * Named snapshots of the Planning-Board view (filters, group,
 * sticky-bar chips, watched/unbilled/hide-empty, accounts mode).
 * Stored server-side under the current user so favourite views
 * follow them across devices and browsers. A localStorage mirror
 * (`co_planning_templates_cache`) is used as an offline / instant
 * bootstrap so the dropdown feels snappy while the network fetch
 * is in-flight.
 *
 *   GET    /me/board-templates
 *   POST   /me/board-templates       { name, state, workspace_id }
 *   DELETE /me/board-templates/{id}
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, ChevronDown, Trash2, Check, Cloud, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

const CACHE_KEY = "co_planning_templates_cache";

function loadCache() {
  try { const raw = localStorage.getItem(CACHE_KEY); return raw ? JSON.parse(raw) : []; }
  catch { return []; }
}
function saveCache(list) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(list || [])); } catch { /* quota */ }
}

export default function BoardTemplatesButton({ getCurrentState, applyState, workspaceId }) {
  const [open, setOpen] = useState(false);
  // Bootstrap from cache so the dropdown never blinks empty.
  const [templates, setTemplates] = useState(loadCache);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/board-templates");
      const list = Array.isArray(data) ? data : [];
      setTemplates(list);
      saveCache(list);
    } catch { /* silent — we already have the cache */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveCurrent = async () => {
    const name = (window.prompt("Name this board view (e.g. \"Weekly PM Review\")") || "").trim();
    if (!name) return;
    const state = getCurrentState();
    setBusy(true);
    try {
      await api.post("/me/board-templates", { name, state, workspace_id: workspaceId || "" });
      toast.success(`Saved "${name}"`);
      await refresh();
      setOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const apply = (t) => {
    applyState(t.state || {});
    toast.success(`Applied "${t.name}"`);
    setOpen(false);
  };

  const remove = async (t, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/me/board-templates/${t.id}`);
      toast.success(`Deleted "${t.name}"`);
      await refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs px-2 h-8 border border-border bg-white hover:bg-neutral-50 inline-flex items-center gap-1"
        title="Save current board view or switch to a saved template (synced across your devices)"
        data-testid="planning-templates-btn"
      >
        <Bookmark className="w-3 h-3" /> Templates
        {templates.length > 0 && <span className="text-[9px] text-muted-foreground">({templates.length})</span>}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[80]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-72 border border-border bg-white shadow-lg z-[90]" data-testid="planning-templates-menu">
            <button
              onClick={saveCurrent}
              disabled={busy}
              className="w-full text-left px-3 py-2 text-xs hover:bg-primary/10 border-b border-border font-semibold text-primary flex items-center gap-1 disabled:opacity-60"
              data-testid="planning-template-save"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />}
              Save current view as…
            </button>
            <div className="flex items-center justify-between px-3 py-1 border-b border-border bg-muted/40 text-[9px] uppercase tracking-widest text-muted-foreground">
              <span className="flex items-center gap-1"><Cloud className="w-3 h-3" /> Synced across your devices</span>
              {loading && <Loader2 className="w-3 h-3 animate-spin" />}
            </div>
            {templates.length === 0 && !loading ? (
              <div className="px-3 py-3 text-xs text-muted-foreground italic">No templates yet. Set up filters &amp; grouping, then save.</div>
            ) : (
              <ul className="max-h-64 overflow-y-auto">
                {templates.map((t) => (
                  <li key={t.id} className="group">
                    <button
                      onClick={() => apply(t)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-muted flex items-center gap-2"
                      data-testid={`planning-template-apply-${t.id}`}
                    >
                      <Check className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                      <span className="flex-1 truncate">{t.name}</span>
                      <span onClick={(e) => remove(t, e)}
                            className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700 p-0.5"
                            data-testid={`planning-template-remove-${t.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
